import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface DateRangeContextType {
  startDate: string
  endDate: string
  setStartDate: (date: string) => void
  setEndDate: (date: string) => void
  setDateRange: (start: string, end: string) => void
  comparisonStartDate: string
  comparisonEndDate: string
  daysDiff: number
  comparisonLabel: string
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined)

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [comparisonStartDate, setComparisonStartDate] = useState('')
  const [comparisonEndDate, setComparisonEndDate] = useState('')
  const [daysDiff, setDaysDiff] = useState(0)
  const [comparisonLabel, setComparisonLabel] = useState('')

  // Initialize with last 7 days (yesterday going back 7 days, excluding today)
  useEffect(() => {
    const today = new Date()
    const yesterday = new Date()
    yesterday.setDate(today.getDate() - 1)
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(yesterday.getDate() - 6) // 7 days including yesterday

    const startStr = sevenDaysAgo.toISOString().split('T')[0]
    const endStr = yesterday.toISOString().split('T')[0]

    setStartDate(startStr)
    setEndDate(endStr)
  }, [])

  // Calculate comparison period whenever dates change
  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate)
      const end = new Date(endDate)
      const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
      setDaysDiff(diff)

      // Calculate comparison period based on duration
      let compStart: Date
      let compEnd: Date
      let label: string

      // Always compare to the same duration in the past
      compEnd = new Date(start)
      compEnd.setDate(compEnd.getDate() - 1) // Day before start
      compStart = new Date(compEnd)
      compStart.setDate(compStart.getDate() - diff + 1)

      // Dynamic label based on number of days
      if (diff === 1) {
        label = 'Vorige Dag'
      } else if (diff <= 7) {
        label = `Vorige ${diff} Dagen`
      } else if (diff <= 14) {
        label = `Vorige ${diff} Dagen`
      } else if (diff <= 31) {
        label = `Vorige ${diff} Dagen`
      } else if (diff <= 90) {
        label = `Vorige ${diff} Dagen`
      } else {
        label = `Vorige ${diff} Dagen`
      }

      const compStartStr = compStart.toISOString().split('T')[0]
      const compEndStr = compEnd.toISOString().split('T')[0]

      setComparisonStartDate(compStartStr || '')
      setComparisonEndDate(compEndStr || '')
      setComparisonLabel(label)
    }
  }, [startDate, endDate])

  const setDateRange = (start: string, end: string) => {
    setStartDate(start)
    setEndDate(end)
  }

  return (
    <DateRangeContext.Provider
      value={{
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        setDateRange,
        comparisonStartDate,
        comparisonEndDate,
        daysDiff,
        comparisonLabel,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  )
}

export function useDateRange() {
  const context = useContext(DateRangeContext)
  if (context === undefined) {
    throw new Error('useDateRange must be used within a DateRangeProvider')
  }
  return context
}
